/**
 * Where each photo's camera position comes from, as the acquisition's preprocess run decided it
 * (forestum `preprocess-drone-photos`, image 1.4 and later).
 *
 * The run's summary comes with the report (`positioning` of its object); the per photo table
 * `positioning.csv` sits beside `photos.gpkg` and the browser reads it the same way. Since image
 * 1.5.10 the CSV says the source itself (`ppk_fixed` / `ppk_float` / `ppk_single`,
 * `anchored_overlap` / `anchored_takeoff`, `takeoff_point` / `takeoff_first_photo`, `rtk_fixed`,
 * `exif`) and the accuracy written for ODM. An older table says only `ppk`, `rtk_fixed`,
 * `anchored` or `exif`: the photo points add the PPK solution's quality (`ppk_quality`), the
 * flight's summary which way an anchored flight was anchored, and the run's take-off heights
 * stand for the photos it calls `exif`.
 */
import type { Feature, FeatureCollection, Point } from 'geojson';
import type { PhotoProperties } from './photoSource';

export type PositionSource = 'rtk' | 'ppk_fixed' | 'ppk_float' | 'ppk_single' | 'anchored_overlap' | 'anchored_takeoff' | 'takeoff_point' | 'takeoff_first_photo' | 'exif';

/** Best first: the order of the legend and the section's figures. */
export const POSITION_SOURCES: PositionSource[] = ['rtk', 'ppk_fixed', 'ppk_float', 'ppk_single', 'anchored_overlap', 'anchored_takeoff', 'takeoff_point', 'takeoff_first_photo', 'exif'];

/**
 * The sources only `positioning.csv` of image 1.5.10 and later writes, taken as they are. Its
 * `rtk_fixed` and `exif` an older table wrote too (`exif` for a height from the take-off), so those
 * go the way of the photo points.
 */
const CSV_SOURCES: Partial<Record<string, PositionSource>> = {
  ppk_fixed: 'ppk_fixed',
  ppk_float: 'ppk_float',
  ppk_single: 'ppk_single',
  anchored_overlap: 'anchored_overlap',
  anchored_takeoff: 'anchored_takeoff',
  takeoff_point: 'takeoff_point',
  takeoff_first_photo: 'takeoff_first_photo',
};

/** The per photo table beside `photos.gpkg`. */
export const POSITIONING_FILE = 'positioning.csv';

/** RTKLIB's fixed and float solutions (forestum `gnss.ppk.QUALITY_FIXED`); any other is a single point. */
const PPK_QUALITY_FIXED = 1;
const PPK_QUALITY_FLOAT = 2;
/** DJI's `RtkFlag` of a fixed solution. */
const RTK_FIXED_FLAG = 50;
/**
 * The accuracy (horizontal, vertical; m) the preprocess gives a camera height from the take-off
 * (forestum `CAMERA_POSITIONS_ACCURACY_M`), for a run that neither wrote it in `positioning.csv`
 * nor said it as `takeoff_accuracy_m` (before image 1.5.10).
 */
export const TAKEOFF_ACCURACY_M: [number, number] = [2, 3];

/** What the map and the page read of the run's summary (the API's `PhotoPositioning`). */
export interface PhotoPositioningSummary {
  positions_file: boolean;
  camera_positions: boolean;
  altitude_datum: string | null;
  takeoff_from_first_photo: boolean;
  /** Image 1.5.10 and later, when a height rests on the take-off. */
  takeoff_accuracy_m?: { horizontal: number; vertical: number } | null;
  flights: { name: string; anchored_by: string | null }[];
}

export interface PositioningRow {
  source: string;
  accH: number | null;
  accV: number | null;
}

const num = (v: string | undefined) => {
  if (v === undefined || v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** One CSV line into its fields; a field in double quotes may hold a comma or a doubled quote. */
function fields(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i]!;
    if (quoted) {
      if (c === '"' && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (c === '"') quoted = false;
      else cur += c;
    } else if (c === '"') quoted = true;
    else if (c === ',') {
      out.push(cur);
      cur = '';
    } else cur += c;
  }
  out.push(cur);
  return out;
}

/** `positioning.csv` by photo path; a table without its `path` and `source` columns is empty. */
export function parsePositioningCsv(text: string): Map<string, PositioningRow> {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== '');
  const out = new Map<string, PositioningRow>();
  if (!lines.length) return out;
  const head = fields(lines[0]!);
  const at = (name: string) => head.indexOf(name);
  const [path, source, accH, accV] = [at('path'), at('source'), at('acc_h_m'), at('acc_v_m')];
  if (path < 0 || source < 0) return out;
  for (const line of lines.slice(1)) {
    const f = fields(line);
    if (!f[path]) continue;
    out.set(f[path]!, { source: f[source] ?? '', accH: accH < 0 ? null : num(f[accH]), accV: accV < 0 ? null : num(f[accV]) });
  }
  return out;
}

export async function loadPositioning(url: string, fetchImpl: typeof fetch = fetch): Promise<Map<string, PositioningRow>> {
  const response = await fetchImpl(url);
  if (!response.ok) throw new Error(`bucket ${response.status}`);
  return parsePositioningCsv(await response.text());
}

/** The take-off heights the run wrote, if any: from the take-off point, or from each flight's first photo. */
function takeoffSource(summary: PhotoPositioningSummary): PositionSource | null {
  if (!summary.camera_positions) return null;
  if (summary.altitude_datum === 'takeoff_point') return 'takeoff_point';
  if (summary.altitude_datum === 'takeoff_relative' && summary.takeoff_from_first_photo) return 'takeoff_first_photo';
  return null;
}

/** Where one photo's position comes from: as the table says it, else in the precedence of forestum's `write_camera_positions`. */
export function positionSourceOf(photo: PhotoProperties, row: PositioningRow | undefined, summary: PhotoPositioningSummary, anchoredBy: Map<string, string | null>): PositionSource {
  const own = row ? CSV_SOURCES[row.source] : undefined;
  if (own) return own;
  const quality = photo.ppk_quality ?? null;
  if (row?.source === 'ppk' || quality !== null) return quality === null || quality === PPK_QUALITY_FIXED ? 'ppk_fixed' : quality === PPK_QUALITY_FLOAT ? 'ppk_float' : 'ppk_single';
  if (row?.source === 'anchored' || (photo.anchor_offset_m ?? null) !== null) {
    return anchoredBy.get(photo.flight ?? '') === 'takeoff' ? 'anchored_takeoff' : 'anchored_overlap';
  }
  const takeoff = takeoffSource(summary);
  if (takeoff && typeof photo.agl_m === 'number') return takeoff;
  if (row?.source === 'rtk_fixed' || photo.rtk_flag === RTK_FIXED_FLAG) return 'rtk';
  return 'exif';
}

/**
 * Its accuracy: as the table wrote it, else (no table) an RTK photo's own estimate. A height from the
 * take-off that an older table wrote with its EXIF accuracy gets the run's take-off accuracy.
 */
export function positionAccuracyOf(photo: PhotoProperties, source: PositionSource, row: PositioningRow | undefined, takeoffAccuracy?: [number, number] | null): [number | null, number | null] {
  if (source === 'takeoff_point' || source === 'takeoff_first_photo') {
    if (row && CSV_SOURCES[row.source] === source && (row.accH !== null || row.accV !== null)) return [row.accH, row.accV];
    return takeoffAccuracy ?? TAKEOFF_ACCURACY_M;
  }
  if (row && (row.accH !== null || row.accV !== null)) return [row.accH, row.accV];
  if (source === 'rtk') return [photo.geo_error_h_m, photo.geo_error_v_m];
  return [null, null];
}

/** The run's take-off accuracy (horizontal, vertical; m), `null` when it said none. */
export function takeoffAccuracyOf(summary: Pick<PhotoPositioningSummary, 'takeoff_accuracy_m'>): [number, number] | null {
  const acc = summary.takeoff_accuracy_m;
  return acc ? [acc.horizontal, acc.vertical] : null;
}

/** The photo points with their position's source and accuracy (`position_source`, `position_acc_h_m`, `position_acc_v_m`). */
export function withPositionSources(
  fc: FeatureCollection<Point, PhotoProperties>,
  summary: PhotoPositioningSummary,
  rows: Map<string, PositioningRow> | null,
): FeatureCollection<Point, PhotoProperties> {
  const anchoredBy = new Map(summary.flights.map((f) => [f.name, f.anchored_by]));
  const takeoffAccuracy = takeoffAccuracyOf(summary);
  const features = fc.features.map((f): Feature<Point, PhotoProperties> => {
    const row = rows?.get(f.properties.path);
    const source = positionSourceOf(f.properties, row, summary, anchoredBy);
    const [h, v] = positionAccuracyOf(f.properties, source, row, takeoffAccuracy);
    return { ...f, properties: { ...f.properties, position_source: source, position_acc_h_m: h, position_acc_v_m: v } };
  });
  return { ...fc, features };
}
